import { useCallback, useEffect, useState } from 'react';

interface UseLocalStorageParams<T> {
  key: string;
  namespace?: string;
  initialValue?: T;
  /**
   * When true, the value starts as `null` on the server / first render and is
   * hydrated from localStorage in an effect — avoids SSR hydration mismatches.
   */
  supportSSR?: boolean;
}

const canUseStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * Envelope used by `@infinex/ui-utils/storage` (the previous storage hook).
 * We keep reading/writing it so existing persisted values stay compatible.
 */
interface StorageEnvelope<T> {
  value: T;
  expiration: number | null;
}

const isEnvelope = <T>(parsed: unknown): parsed is StorageEnvelope<T> =>
  typeof parsed === 'object' &&
  parsed !== null &&
  'value' in parsed &&
  'expiration' in parsed;

/**
 * Minimal namespaced localStorage hook for the dataset debugger. Stores the
 * value under `${namespace}:${key}` in the same `{ value, expiration }`
 * envelope the previous `@infinex/ui-utils/storage` hook used, so values
 * persisted before this refactor still load. No cross-tab sync — the debugger
 * doesn't need it.
 */
export function useLocalStorage<T>({
  key,
  namespace = 'default',
  initialValue,
  supportSSR = false,
}: UseLocalStorageParams<T>): [T | null, (value: T) => void] {
  const fullKey = `${namespace}:${key}`;

  const read = useCallback((): T | null => {
    if (!canUseStorage()) return initialValue ?? null;
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw === null) return initialValue ?? null;
      const parsed: unknown = JSON.parse(raw);
      if (isEnvelope<T>(parsed)) {
        if (parsed.expiration !== null && parsed.expiration <= Date.now()) {
          localStorage.removeItem(fullKey);
          return initialValue ?? null;
        }
        return parsed.value;
      }
      // Tolerate a bare (un-enveloped) value for forward/backward safety.
      return parsed as T;
    } catch {
      return initialValue ?? null;
    }
  }, [fullKey, initialValue]);

  const [storedValue, setStoredValue] = useState<T | null>(() =>
    supportSSR ? null : read()
  );

  // In SSR mode the first render is `null`; hydrate from storage on the client.
  useEffect(() => {
    if (supportSSR) setStoredValue(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback(
    (value: T) => {
      setStoredValue(value);
      if (!canUseStorage()) return;
      try {
        const envelope: StorageEnvelope<T> = { value, expiration: null };
        localStorage.setItem(fullKey, JSON.stringify(envelope));
      } catch {
        // localStorage unavailable / quota exceeded — ignore
      }
    },
    [fullKey]
  );

  return [storedValue, setValue];
}
